/**
 * HostPoint POS - Idle Timeout Service
 * Auto-logout users after configurable period of inactivity
 */

import { useEffect, useRef, useCallback, useState } from 'react';

// Default idle timeout options (in milliseconds)
export const IDLE_TIMEOUT_OPTIONS = {
  '1': { label: '1 minute', value: 1 * 60 * 1000 },
  '3': { label: '3 minutes', value: 3 * 60 * 1000 },
  '5': { label: '5 minutes', value: 5 * 60 * 1000 },
  '10': { label: '10 minutes', value: 10 * 60 * 1000 },
  '15': { label: '15 minutes', value: 15 * 60 * 1000 },
  '30': { label: '30 minutes', value: 30 * 60 * 1000 },
  'never': { label: 'Never', value: null }
};

// Storage key for idle timeout setting
const IDLE_TIMEOUT_KEY = 'hostpoint_idle_timeout';
const LAST_ACTIVITY_KEY = 'hostpoint_last_activity';
const SESSION_START_KEY = 'hostpoint_session_start';

/**
 * Get the configured idle timeout
 */
export function getIdleTimeoutSetting() {
  const saved = localStorage.getItem(IDLE_TIMEOUT_KEY);
  return saved || '5'; // Default to 5 minutes
}

/**
 * Set the idle timeout setting
 */
export function setIdleTimeoutSetting(value) {
  localStorage.setItem(IDLE_TIMEOUT_KEY, value);
}

/**
 * Get the idle timeout value in milliseconds
 */
export function getIdleTimeoutMs() {
  const setting = getIdleTimeoutSetting();
  return IDLE_TIMEOUT_OPTIONS[setting]?.value || IDLE_TIMEOUT_OPTIONS['5'].value;
}

/**
 * Update last activity timestamp
 */
export function updateLastActivity() {
  localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
}

/**
 * Mark session start (called on login)
 */
export function markSessionStart() {
  const now = Date.now().toString();
  localStorage.setItem(SESSION_START_KEY, now);
  localStorage.setItem(LAST_ACTIVITY_KEY, now);
}

/**
 * Clear session markers (called on logout)
 */
export function clearSessionMarkers() {
  localStorage.removeItem(SESSION_START_KEY);
  localStorage.removeItem(LAST_ACTIVITY_KEY);
}

/**
 * Get session start time
 */
export function getSessionStart() {
  const saved = localStorage.getItem(SESSION_START_KEY);
  return saved ? parseInt(saved, 10) : null;
}

/**
 * Get last activity timestamp
 */
export function getLastActivity() {
  const saved = localStorage.getItem(LAST_ACTIVITY_KEY);
  return saved ? parseInt(saved, 10) : Date.now();
}

/**
 * Check if session has expired due to inactivity
 * @param {number} gracePeriodMs - Grace period in ms for new sessions (default 10 seconds)
 */
export function isSessionExpired(gracePeriodMs = 10000) {
  const timeoutMs = getIdleTimeoutMs();
  if (!timeoutMs) return false; // Never timeout
  
  // Check if this is a fresh session (within grace period)
  const sessionStart = getSessionStart();
  if (sessionStart) {
    const sessionAge = Date.now() - sessionStart;
    if (sessionAge < gracePeriodMs) {
      // Fresh session, don't expire
      return false;
    }
  }
  
  const lastActivity = getLastActivity();
  const elapsed = Date.now() - lastActivity;
  
  return elapsed > timeoutMs;
}

/**
 * Hook for idle timeout management
 * @param {Function} onIdle - Callback when user becomes idle (for logout)
 * @param {boolean} enabled - Whether idle detection is enabled
 */
export function useIdleTimeout(onIdle, enabled = true) {
  const timeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const [showWarning, setShowWarning] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);

  const resetTimer = useCallback(() => {
    const timeoutMs = getIdleTimeoutMs();
    
    // Clear existing timers
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
    }
    
    // Hide warning if shown
    setShowWarning(false);
    
    // Update last activity
    updateLastActivity();
    
    // Don't set timer if timeout is disabled
    if (!timeoutMs || !enabled) return;
    
    // Warning time (30 seconds before logout)
    const warningTime = Math.max(timeoutMs - 30000, timeoutMs * 0.9);
    
    // Set warning timer
    warningTimeoutRef.current = setTimeout(() => {
      setShowWarning(true);
      setRemainingTime(30);
      
      // Countdown
      const countdownInterval = setInterval(() => {
        setRemainingTime((prev) => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      // Store interval reference for cleanup
      warningTimeoutRef.current = countdownInterval;
    }, warningTime);
    
    // Set logout timer
    timeoutRef.current = setTimeout(() => {
      setShowWarning(false);
      onIdle?.();
    }, timeoutMs);
  }, [onIdle, enabled]);

  const handleActivity = useCallback(() => {
    resetTimer();
  }, [resetTimer]);

  // Extend session (called when user clicks "Stay Logged In")
  const extendSession = useCallback(() => {
    setShowWarning(false);
    resetTimer();
  }, [resetTimer]);

  useEffect(() => {
    if (!enabled) {
      // Clear timers if disabled
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
      return;
    }

    // Check if session already expired on mount
    if (isSessionExpired()) {
      onIdle?.();
      return;
    }

    // Events that indicate user activity
    const events = [
      'mousedown',
      'mousemove',
      'keydown',
      'scroll',
      'touchstart',
      'touchmove',
      'click',
      'wheel'
    ];

    // Add event listeners
    events.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Cleanup
    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
        clearInterval(warningTimeoutRef.current);
      }
    };
  }, [enabled, handleActivity, onIdle]);

  // Initial timer setup - separate effect to avoid lint warning
  useEffect(() => {
    if (enabled && !isSessionExpired()) {
      const timerId = setTimeout(() => {
        resetTimer();
      }, 0);
      return () => clearTimeout(timerId);
    }
  }, [enabled, resetTimer]);

  return {
    showWarning,
    remainingTime,
    extendSession,
    resetTimer
  };
}

/**
 * Hook for idle timeout settings management
 */
export function useIdleTimeoutSettings() {
  const [setting, setSetting] = useState(getIdleTimeoutSetting());

  const updateSetting = useCallback((value) => {
    setIdleTimeoutSetting(value);
    setSetting(value);
  }, []);

  return {
    setting,
    updateSetting,
    options: IDLE_TIMEOUT_OPTIONS
  };
}

export default {
  useIdleTimeout,
  useIdleTimeoutSettings,
  getIdleTimeoutSetting,
  setIdleTimeoutSetting,
  getIdleTimeoutMs,
  isSessionExpired,
  updateLastActivity,
  markSessionStart,
  clearSessionMarkers,
  getSessionStart,
  IDLE_TIMEOUT_OPTIONS
};
